import React from 'react';
import PlusIcon from '../../../../../assets/icons/plus-icon.svg';
import { Button } from '../../../../../components/button';
import { Card, CardContent } from '../../../../../components/card';
import { Input } from '../../../../../components/input';
import { Label } from '../../../../../components/label';
import { FormErrorText } from '../../../../../components/form';
import { MerchantCouponUIState } from '../../data/merchantCouponEditor.data';
import type { MerchantCouponValidationErrors } from '../../schemas/merchantCouponEditor.schema';

interface TermsConditionsSectionProps {
    coupon: MerchantCouponUIState;
    onAddTerm: () => void;
    onRemoveTerm: (index: number) => void;
    onUpdateTerm: (index: number, value: string) => void;
    isCreating: boolean;
    errors: MerchantCouponValidationErrors;
}

export const TermsConditionsSection: React.FC<TermsConditionsSectionProps> = ({
    coupon,
    onAddTerm,
    onRemoveTerm,
    onUpdateTerm,
    isCreating,
    errors,
}) => {
    return (
        <Card className="border border-[#e6e6e6] dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[10px]">
            <CardContent className="px-[24.8px] py-[24.8px]">
                {/* Header */}
                <div className="border-b border-[#00c49a] dark:border-emerald-500 border-b-[0.8px] pb-[8.8px] mb-6">
                    <h2 className="text-[18px] font-normal text-[#004232] dark:text-white leading-[27px]">
                        Terms & Conditions
                    </h2>
                </div>

                <div className="space-y-4">
                    <p className="text-[13px] text-[#6b7280] dark:text-gray-400 leading-[20px]">
                        This information is displayed to users as reference only. The system does not automatically validate these conditions.
                    </p>
                    {coupon.terms_and_conditions.map((term, index) => (
                        <div key={index} className="flex gap-4 items-end">
                            <div className="flex-1 space-y-2">
                                <Label className="text-[14px] font-normal text-[#75757d] dark:text-gray-300 leading-[21px]">
                                    Term #{index + 1} <span className="text-[#FB2C36]">*</span>
                                </Label>
                                <Input
                                    value={term}
                                    onChange={(e) => onUpdateTerm(index, e.target.value)}
                                    disabled={isCreating}
                                    placeholder="Enter term or condition..."
                                    className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[8px] px-[16.8px] py-[10px] text-[14px] bg-white dark:bg-gray-900 text-foreground"
                                />
                            </div>
                            {coupon.terms_and_conditions.length > 1 && (
                                <Button
                                    variant="outline"
                                    onClick={() => onRemoveTerm(index)}
                                    disabled={isCreating}
                                    className="h-[40px] px-4 py-[9.6px] border border-[#004232] dark:border-emerald-500 rounded-[6px] bg-transparent dark:bg-transparent text-[14px] font-medium text-[#004232] dark:text-emerald-400 hover:bg-[#f0f9f5] dark:hover:bg-emerald-900/30 transition-colors"
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                    ))}
                    <FormErrorText message={errors.terms_and_conditions} />

                    {/* Add Another Term Button */}
                    <div className="flex justify-end">
                        <Button
                            variant="outline"
                            onClick={onAddTerm}
                            disabled={isCreating}
                            className="h-[40px] px-[10px] py-[9.6px] border border-[#004232] dark:border-emerald-500 rounded-[6px] bg-transparent dark:bg-transparent text-[14px] font-medium text-[#004232] dark:text-emerald-400 hover:bg-[#f0f9f5] dark:hover:bg-emerald-900/30 gap-2 transition-colors"
                        >
                            <img src={PlusIcon} alt="Add" className="w-5 h-5 dark:invert dark:opacity-80" />
                            <span>Add another term</span>
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
